<script setup lang="ts">
import { computed, ref, watch, inject, type ComputedRef } from 'vue'

import GbButton from '@/components/GbButton.vue';
import ResourceDownloadButton from '@/resources/ResourceDownloadButton.vue'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper.vue'
import { truncateTextWithEllipsis } from '@/util/strings'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { PRESENTLEVELS_EVENT_NAMES } from '@/presentlevels/event_names.ts'
import { toolkitUrls } from '@/util/links.js'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable.js'

const props = defineProps({
  resource: {
    type: Object,
    required: true
  }
})

const subjectLabelRef = inject<ComputedRef<string>>('subjectLabel', computed(() => ''))

const emit = defineEmits(['close', 'showResourcePreviewModal', 'showSaveToFolderModal'])

const PLW_TO_ALGOLIA_SUBJECT_MAP: Record<string, string> = {
  "Reading": "Reading",
  "Writing": "Writing",
  "Math": "Math",
  "Alt Academics: Reading": "Alt Academics: Reading",
  "Alt Academics: Writing": "Alt Academics: Writing",
  "Alt Academics: Math": "Alt Academics: Math",
  "Executive Functioning": "Executive Functioning",
  "Behavior": "Behavior & SEL",
  "Social & Emotional Learning": "Behavior & SEL",
  "Social Skills": "Behavior & SEL",
  "Speech & Language": "Speech & Language",
  "Fine Motor": "Fine Motor",
  "Gross Motor": "Gross Motor",
  "Life Skills": "Life Skills",
  "Transition": "Transition",
  "Pre-Academics": "Pre-Academics"
}

const imageUrl = computed(() => props.resource.thumbnail_url)

const isDescriptionExpanded = ref(false)
const fullDescription = computed(() => props.resource?.description || '')
const displayDescription = computed(() => {
  if (isDescriptionExpanded.value) {
    return fullDescription.value
  }
  return truncateTextWithEllipsis(fullDescription.value, 190)
})
watch(() => props.resource?.description, (newDesc, oldDesc) => {
  if (newDesc !== oldDesc) {
    isDescriptionExpanded.value = false
  }
})

const handleClose = () => {
  emit('close')
}

const analytics = getAnalyticsTracker().getChildContext({
  resource_uuid: props.resource.uuid,
  resource_title: props.resource.title,
  resource_type: props.resource.type,
  resource_grades: props.resource.grades,
  resource_subjects: props.resource.subjects,
  resource_language: props.resource.language,
  resource_preview_type: 'popover'
})

function handlePreview() {
  emit('showResourcePreviewModal')
  analytics.sendEvent(EVENT_NAMES.RESOURCES.SHOWED_PREVIEW, {
    resource_preview_type: 'modal'
  })
}

function handleSave() {
  emit('showSaveToFolderModal')
  analytics.sendEvent(EVENT_NAMES.RESOURCES.CLICKED_SAVE)
}


const resourceSubject = computed(() => {
  const subjectLabel = subjectLabelRef.value
  if (!subjectLabel) return ''
  const algoliaSubject = PLW_TO_ALGOLIA_SUBJECT_MAP[subjectLabel] || subjectLabel
  return getGoalSubjectLabelFromValue(algoliaSubject)
})

const browseAssessmentsUrl = computed(() => {
  const finalSubject = resourceSubject.value
  if (!finalSubject) return ''
  return `${toolkitUrls.browseAssessments}?subject=${encodeURIComponent(finalSubject)}`
})

function handleMoreAssessmentsClick() {
  analytics.sendEvent(PRESENTLEVELS_EVENT_NAMES.RESOURCES.MORE_ASSESSMENTS)
}
</script>

<template>
  <div class="flex flex-col">
    <div class="flex bg-gray-200 p-2 justify-between">
      <span class="text-md font-medium pl-3">{{ resource.title }}</span>
      <GbButton
        size="xs"
        rounded
        :iconOnly="true"
        aria-label="close resource popover"
        gbTheme="grayTertiary"
        @click="handleClose"
        @keydown.space="handleClose"
        @keydown.esc="handleClose"
        class="p-3.5"
      >
        <span class="material-icons-outlined text-xl">close</span>
      </GbButton>
    </div>
    <div class="px-5 py-4">
      <div class="flex justify-start">
        <a
        tabindex="0"
        @click="handlePreview"
        @keyup.enter="handlePreview"
        class="grid hover:cursor-pointer [&_.invisible]:hover:!visible [&_.invisible]:hover:opacity-70"
        :title="`Preview ${resource.title}`"
      >
        <div
          class="invisible z-30 col-span-full row-span-full flex flex-col h-full items-center bg-black uppercase text-white"
        >
          <span class="material-icons-outlined mt-[50%]"> zoom_in </span>
          <span class="grow">Preview</span>
        </div>
        <IKImageS3Wrapper
          :aws-url="imageUrl"
          alt=""
          :transformation="[{ width: 150 }]"
          class="z-20 col-span-full row-span-full border border-gray-600"
        />
      </a>
      </div>
      <div class="flex gap-2 mt-3 mb-5">
        <ResourceDownloadButton
          :resource="resource"
          :condenseButtons="true"
          :showTooltips="true"
        />
        <GbButton
          gbTheme="greenPrimary"
          size="small"
          :aria-label="`Save ${resource.title}`"
          @click="handleSave"
          @keyup.enter="handleSave"
          class="!p-1"
          tooltipText="Save"
        >
          <span class="material-icons-round text-[24px]">folder</span>
        </GbButton>
        <button
          :aria-label="`Preview ${resource.title}`"
          @click="handlePreview"
          @keyup.enter="handlePreview"
          class="text-blue-400 text-md hover:text-blue-500 !px-3"
        >
          Preview
        </button>
      </div>
      <div class="text-sm leading-snug whitespace-pre-wrap">
        <span tabindex="0" aria-live="polite">{{ displayDescription }}</span>
        <button
          v-if="fullDescription.length > 190 && !isDescriptionExpanded"
          @click="isDescriptionExpanded = true"
          class="text-blue-400 hover:text-blue-800 text-sm underline ml-1"
          aria-label="Read more"
        >read more</button>
      </div>
    </div>
    <div v-if="resourceSubject" class="border-t border-gray-600 px-5 py-3">
      <a
        :href="browseAssessmentsUrl"
        target="_blank"
        class="text-blue-400 hover:text-blue-500 text-md inline-flex items-center gap-0.5"
        @click="handleMoreAssessmentsClick"
      >
        <span class="hover:underline">More {{ resourceSubject }} assessments</span>
        <span class="material-icons text-sm">open_in_new</span>
      </a>
    </div>
  </div>
</template>
